<template v-cloak>
    <div class="container is-fluid">
        <div class="columns is-multiline">
            <div class="column is-12 page-title">
                <h1 class="is-size-3">Law360<sup>&reg;</sup> Pulse Leaderboard Analytics</h1>
            </div>
            <div class="column is-3">
                <div class="columns is-multiline">
                    <div v-if="!clearButtonDisabled" class="column is-12">
                        <div class="card p-5">
                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one">SELECT&nbsp;<i
                                    class="fa fa-angle-right is-size-6 has-text-weight-bold"></i>&nbsp;GENERATE</h3>
                            <br>
                            <div class="content is-size-6 has-text-weight-light is-default-font-color">
                                Compare the performance of a primary law firm against one or more firms in a comparison group that you create.
                            </div>
                        </div>
                    </div>
                    <div class="column is-multiline is-12">
                        <div class="card p-5">
                            <Transition name="fade">
                                <div v-if="showSaveSuccess" class="notification is-success">
                                    <button class="delete"></button>
                                    <p class="is-size-6">You have successfully saved your selections!</p>
                                </div>
                            </Transition>
                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one mb-1">FILTERS</h3>
                            <p class="is-size-7 mb-3">Use filters to select a primary law firm and comparison group, or load a previously saved group.</p>
                            <br>
                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one">Select year</h3>
                            <br>
                            <div class="mb-6">
                                <multiselect 
                                    v-model="dataYear" deselect-label="Can't remove this value" placeholder="Select year..." :options="dataYears" :searchable="true" :allow-empty="false" >
                                </multiselect>
                            </div>
                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one">Primary law firm</h3>
                            <br>
                            <div class="mb-6">
                                <multiselect v-model="benchmarkFirm" deselect-label="Can't remove this value"
                                    track-by="editorial_id" label="name" placeholder="Type firm name..." :options="allLawFirms"
                                    :searchable="true" :allow-empty="false" :options-limit="4000" >
                                    <template v-slot:singleLabel="{ option }">{{ option.name }}</template>
                                </multiselect>
                            </div>
                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one mb-1">Comparison group law firms</h3>
                            <div>
                                <p class="mb-2 is-size-7">Up to 8 max.</p>
                                <multiselect v-model="comparisonFirms" :max="8" :options="allLawFirms" :multiple="true"
                                    :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                                    placeholder="Type firm name..." label="name" track-by="editorial_id" :preselect-first="false" :options-limit="4000" >
                                    <template v-slot:singleLabel="{ option }">{{ option.name }}</template>
                                </multiselect>
                            </div>
                            <br>
                            <button @click="fetchAllData();" :disabled="saveButtonDisabled"
                                class="button is-fullwidth mb-3 is-success">Generate Report &nbsp;<i
                                    class="fa fa-arrow-circle-right fa-lg"></i></button>
                            <div class="columns is-multiline">
                                <div class="column is-6">
                                    <button @click="toggleSaveModal" :disabled="saveButtonDisabled"
                                        class="button is-fullwidth"><i class="fa fa-save"></i>&nbsp;Save Selections</button>
                                </div>
                                <div class="column is-6">
                                    <button @click="clearSelections" :disabled="clearButtonDisabled"
                                        class="button is-fullwidth"><i class="fa fa-times"></i>&nbsp;Clear
                                        Selections</button>
                                </div>
                            </div>

                            <div class="column is-12">
                                <p class="has-text-centered is-size-5">---------- OR ----------</p>
                            </div>

                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one">Save Comparison Group</h3>
                            <br>
                            <div class="mb-7">
                                <p class="is-size-6 mb-3">Use a previously saved primary law firm and comparison group. </p>
                                <multiselect v-model="selectedSavedBenchmark" :options="savedBenchmarks" :searchable="true" :multiple="false"
                                    :close-on-select="true" label="name" :show-labels="false" @select="fetchSavedBenchmark"
                                    placeholder="Search saved..."></multiselect>
                            </div>

                        </div>
                        <div class="card p-5 mt-5">
                            <h3 class="is-size-6 has-text-weight-bold is-secondary-one mb-1">DOWNLOAD SOURCE DATA</h3>
                            <br>
                            <div class="column is-full">
                                <a v-if="isSubscriber" href="https://assets.law360news.com/pulse/Law360+Pulse+Leaderboard+Analytics+2024+Full+Data.xlsx" target="_blank">
                                    <button @click="downloadFullData"
                                        class="button is-fullwidth has-tooltip-right has-tooltip-arrow" data-tooltip="Download all source data to csv"><i class="fas fa-download"></i>&nbsp;Download Full Dataset
                                    </button>
                                </a>
                                <a v-else href="/pulse/subscribe" target="_blank">
                                    <button @click="downloadFullData"
                                        class="button is-fullwidth has-tooltip-right has-tooltip-arrow" data-tooltip="Subscribe for full data access"><i class="fas fa-download"></i>&nbsp;Download Full Dataset
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="column is-12">
                        <div class="card is-primary-three-background p-5">
                            <h3 class="is-size-6 has-text-weight-bold is-text-black">DATA METHODOLOGY</h3>
                            <br>
                            <p class="is-size-6">
                                Law360 Pulse<sup>&reg;</sup> Leaderboard Analytics provides a standardized framework for evaluating and
                                comparing law firm performance across key measures. Read <a target="_blank" href="https://assets.law360news.com/pulse/law360-pulse-leaderboard-analytics-methodology-2025.pdf">the methodology document</a> for details on metrics and measurement criteria.

                            </p>
                            <br>
                            <h3 class="is-size-6 has-text-weight-bold is-text-black">CONTACT US</h3>
                            <br>
                            <p class="is-size-6">
                                For data queries, please contact us at: <a href:="mailto:surveys@law360.com">surveys@law360.com</a>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="column is-9 benchmark-wrapper" id="print-section">
                <div class="columns is-multiline">
                    <div class="column tile is-3 show-print is-primary-one-background is-flex is-justify-content-center is-align-items-center">
                        <figure class="image">
                            <img src="/images/law360-pulse-logo-pipe.png" class="is-full-width" alt="pulse logo">
                        </figure>
                    </div>
                    <transition name="fade">
                        <div v-if="clearButtonDisabled" class="column is-12">
                            <div class="starter-bg p-6 column is-12 has-text-white">
                                <h1 class="is-size-3 px-3">Which firms would you like to compare today?</h1>
                                <p class="is-size-5 my-5 px-3">
                                    Compare the performance of a primary law firm against one or more firms in a comparison group that you create.
                                </p>
                                <p class="is-size-5 my-5 px-3">
                                    To get started, please select the name of the primary law firm and comparison firm(s) (or choose a saved group) and click ‘Generate Report.’
                                </p>
                            </div>
                        </div>
                    </transition>
                    <div v-if="!clearButtonDisabled" class="column is-12">
                        <div class="tile is-ancestor">
                            <div class="tile is-parent is-5">
                                <div class="card column p-5 is-secondary-three-background">
                                    <h3 class="is-size-6 has-text-weight-bold is-benchmark-header-color">
                                        Primary Law Firm:
                                    </h3>
                                    <br>
                                    <template v-if="benchmarkFirm">
                                        <h2 class="has-text-white has-text-weight-bold is-size-4">
                                            {{ benchmarkFirm.name }}
                                        </h2>
                                    </template>
                                </div>
                            </div>
                            <div class="tile is-parent is-7">
                                <div class="card column p-5 is-secondary-two-background">
                                    <h3 class="is-size-6 has-text-weight-bold is-comparison-header-color">
                                        Comparison Group Law Firms:
                                    </h3>
                                    <br>
                                    <p class="has-text-white is-size-6">{{ comparsionFirmsConcat }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <div class="p-5 table-container has-background-white">
                            <div class="row p-4">
                                <div class="columns">
                                    <div class="column is-2 is-flex is-flex-direction-column is-justify-content-flex-end ">
                                        <p class="has-text-weight-bold is-size-6">Data shown is for {{this.dataYear}}</p>
                                    </div>
                                    <div class="column is-7"></div>
                                    <template v-if="benchmarkFirmData">

                                        <div
                                            class="column is-3 is-flex is-justify-content-flex-end is-align-items-center hide-print">
                                            <div class="px-5 py-3 mr-5 has-tooltip-left has-tooltip-arrow"
                                                data-tooltip="Download data table to CSV" style="border:1px solid black;">
                                                <span
                                                    @click="fetchBenchmarkCsv"
                                                    class="is-hidden-touch tool is-flex is-justify-content-center is-align-items-center is-size-6">
                                                    <svg class="svg-icon" width="30" height="30" viewBox="0 0 30 30"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                        <g>
                                                            <polygon class="st0"
                                                                points="25.9,18.8 25.9,25 4.1,25 4.1,18.8 2.8,18.8 2.8,26.3 27.2,26.3 27.2,18.8 	" />
                                                            <polygon class="st0"
                                                                points="21.8,14.6 20.9,13.7 15.6,18.9 15.6,3.7 14.4,3.7 14.4,19.2 8.9,13.7 8,14.6 14.9,21.4 	" />
                                                        </g>
                                                    </svg> CSV
                                                </span>

                                            </div>
                                            <div class="px-5 py-3 has-tooltip-left has-tooltip-arrow"
                                                style="border:1px solid black;" data-tooltip="Save data table and charts to PDF">
                                                <span
                                                    class="is-hidden-touch tool is-flex is-justify-content-center is-align-items-center is-size-6"
                                                    @click="print">
                                                    <svg class="svg-icon" width="30" height="30" viewBox="0 0 30 30"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                        <path class="st0" d="M19,3H6v10.1H1.7v7.8H6V27h20V12.8v-0.1V11v-0.6V9.7L19,3z M19.2,4.9l5,4.8h-5V4.9z M4.8,17.7v1.9h-1v-5.1h1.7
                                                    c0.6,0,1,0,1.2,0.1c0.3,0.1,0.5,0.2,0.7,0.5c0.2,0.3,0.3,0.6,0.3,1c0,0.3-0.1,0.6-0.2,0.8c-0.1,0.2-0.3,0.4-0.4,0.5
                                                    c-0.2,0.1-0.4,0.2-0.5,0.2c-0.2,0-0.6,0.1-1.1,0.1H4.8z M24.8,12.7L24.8,12.7v13.1h-0.1H7.2v-4.9h11.9v-7.8H7.2V4.2H18V11h6.8V12.7
                                                    z M8.6,19.6v-5.1h1.9c0.4,0,0.7,0,1,0.1c0.3,0.1,0.6,0.2,0.8,0.5c0.2,0.2,0.4,0.5,0.5,0.8s0.2,0.7,0.2,1.2c0,0.4-0.1,0.8-0.2,1.1
                                                    c-0.1,0.4-0.3,0.7-0.5,0.9c-0.2,0.2-0.4,0.3-0.7,0.4c-0.2,0.1-0.5,0.1-0.9,0.1H8.6z M16.9,16.6v0.9h-2.1v2.2h-1v-5.1h3.5v0.9h-2.5
                                                    v1.2H16.9z" />
                                                        <path class="st0"
                                                            d="M11.8,17.1c0-0.4,0-0.7-0.1-0.9c-0.1-0.2-0.2-0.4-0.3-0.5c-0.1-0.1-0.3-0.2-0.5-0.2c-0.1,0-0.4,0-0.8,0H9.6
                                                    v3.4h0.8c0.3,0,0.5,0,0.6,0c0.2,0,0.3-0.1,0.4-0.2c0.1-0.1,0.2-0.3,0.3-0.5C11.8,17.8,11.8,17.5,11.8,17.1z" />
                                                        <path class="st0"
                                                            d="M6.5,15.7c-0.1-0.1-0.2-0.2-0.4-0.2c-0.1,0-0.4,0-0.7,0H4.8v1.5h0.6c0.4,0,0.7,0,0.8-0.1
                                                    c0.1-0.1,0.2-0.1,0.3-0.3c0.1-0.1,0.1-0.2,0.1-0.4C6.7,15.9,6.6,15.8,6.5,15.7z" />
                                                    </svg> PDF
                                                </span>

                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <div class="row p-4">
                                <div class="tabs is-boxed">
                                    <ul>
                                        <li @click="showTab('overview')" :class="{ 'is-active': currentTab === 'overview' }" class="has-text-primary-one"><a class="has-text-primary-one">Overview</a></li>
                                        <li
                                            @click="benchmarkFirmData && showTab('practiceAreas')"
                                            :class="[{ 'is-active': currentTab === 'practiceAreas' }, { 'is-disabled': !benchmarkFirmData }]"
                                            :aria-disabled="!benchmarkFirmData"
                                            class="has-text-primary-one"
                                        ><a class="has-text-primary-one">Practice Areas</a></li>
                                        <li
                                            @click="benchmarkFirmData && showTab('locations')"
                                            :class="[{ 'is-active': currentTab === 'locations' }, { 'is-disabled': !benchmarkFirmData }]"
                                            :aria-disabled="!benchmarkFirmData"
                                            class="has-text-primary-one"
                                        ><a class="has-text-primary-one">Locations</a></li>
                                    </ul>
                                </div>
                            </div>
                            <div class="overview-wrapper" v-if="currentTab === 'overview'">
                                <table class="table is-borderd is-striped pulse-table overview-table">
                                <thead class="table-light">
                                    <tr>
                                        <th rowspan="2">Comparison indicator</th>
                                        <th rowspan="2" class="benchmark">Primary law firm</th>
                                        <th colspan="2" class="comparison">Comparison group firms</th>
                                        <th colspan="2" class="benchmark">Primary law firm vs. comparison group firms</th>
                                    </tr>
                                    <tr>
                                        <th class="comparison">
                                            Median avg. <i class="fa fa-info-circle" data-bs-toggle="modal"
                                                data-bs-target=".bs-modal-tooltips" @click="toggleInfoModal"></i>
                                        </th>
                                        <th class="comparison">
                                            Mean avg. <i class="fa fa-info-circle" data-bs-toggle="modal"
                                                data-bs-target=".bs-modal-tooltips"  @click="toggleInfoModal"></i>
                                        </th>
                                        <th class="benchmark">
                                            Trend vs. mean avg. <i class="fa fa-info-circle" data-bs-toggle="modal"
                                                data-bs-target=".bs-modal-tooltips"  @click="toggleInfoModal"></i>
                                        </th>
                                        <th class="benchmark">
                                            Variance vs. mean avg. <i class="fa fa-info-circle" data-bs-toggle="modal"
                                                data-bs-target=".bs-modal-tooltips"  @click="toggleInfoModal"></i>
                                        </th>
                                    </tr>
                                    <!-- end tr -->
                                </thead>
                                <!-- end thead -->
                                <tbody>
                                    <tr>
                                        <td>
                                            Total number of lawyers
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.totalAttorneys" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmSanitized.totalAttorneys }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalAttorneysMedian" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalAttorneysMedian }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalAttorneysMean" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalAttorneysMean }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.totalAttorneysTrend == 'up'" class="trend-up">
                                                    ABOVE <i class="fa fa-arrow-up"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalAttorneysTrend == 'down'" class="trend-down">
                                                    BELOW <i class="fa fa-arrow-down"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalAttorneysTrend == 'neutral'" class="trend-neutral">
                                                    NEUTRAL <i class="fa fa-arrow-right"></i>
                                                </p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalAttorneysVar" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalAttorneysVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Number of US lawyers
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.totalUSAttorneysFirm" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmSanitized.totalUSAttorneysFirm }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalUSAttorneysFirmMedian" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalUSAttorneysFirmMedian }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalUSAttorneysFirmMean" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalUSAttorneysFirmMean }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.totalUSAttorneysFirmTrend == 'up'" class="trend-up">
                                                    ABOVE <i class="fa fa-arrow-up"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalUSAttorneysFirmTrend == 'down'" class="trend-down">
                                                    BELOW <i class="fa fa-arrow-down"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalUSAttorneysFirmTrend == 'neutral'" class="trend-neutral">
                                                    NEUTRAL <i class="fa fa-arrow-right"></i>
                                                </p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalUSAttorneysFirmVar" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalUSAttorneysFirmVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Number of US partners
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.totalUSPartnersFirm" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmSanitized.totalUSPartnersFirm }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalUSPartnersFirmMedian" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalUSPartnersFirmMedian }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalUSPartnersFirmMean" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalUSPartnersFirmMean }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.totalUSPartnersFirmTrend == 'up'" class="trend-up">
                                                    ABOVE <i class="fa fa-arrow-up"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalUSPartnersFirmTrend == 'down'" class="trend-down">
                                                    BELOW <i class="fa fa-arrow-down"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalUSPartnersFirmTrend == 'neutral'" class="trend-neutral">
                                                    NEUTRAL <i class="fa fa-arrow-right"></i>
                                                </p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalUSPartnersFirmVar" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalUSPartnersFirmVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Number of non-US lawyers
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.totalNonUSAttorneysFirm" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmSanitized.totalNonUSAttorneysFirm }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalNonUSAttorneysFirmMedian" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalNonUSAttorneysFirmMedian }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalNonUSAttorneysFirmMean" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalNonUSAttorneysFirmMean }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.totalNonUSAttorneysFirmTrend == 'up'" class="trend-up">
                                                    ABOVE <i class="fa fa-arrow-up"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalNonUSAttorneysFirmTrend == 'down'" class="trend-down">
                                                    BELOW <i class="fa fa-arrow-down"></i>
                                                </p>
                                                <p v-else-if="comparisonFirmsSanitized.totalNonUSAttorneysFirmTrend == 'neutral'" class="trend-neutral">
                                                    NEUTRAL <i class="fa fa-arrow-right"></i>
                                                </p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.totalNonUSAttorneysFirmVar" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.totalNonUSAttorneysFirmVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr style="border-top:2px solid black;">
                                        <td>
                                            Gross revenue
                                            <span class="text-muted mb-0 font-size-11">($M)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.grossRevenue" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.grossRevenue }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.grossRevenueMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.grossRevenueMedian
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.grossRevenueMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.grossRevenueMean
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.grossRevenueTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.grossRevenueTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.grossRevenueTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.grossRevenueVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.grossRevenueVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Revenue change vs. prior year
                                            <span class="text-muted mb-0 font-size-11">(%)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.revenueChange" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.revenueChange }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.revenueChangeMedian"
                                                    class="mb-1 font-size-13">{{
                                                        comparisonFirmsSanitized.revenueChangeMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.revenueChangeMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.revenueChangeMean
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.revenueChangeTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.revenueChangeTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.revenueChangeTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.revenueChangeVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.revenueChangeVar
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Net income
                                            <span class="text-muted mb-0 font-size-11">($M)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.netIncome" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.netIncome }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.netIncomeMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.netIncomeMedian }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.netIncomeMean" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.netIncomeMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.netIncomeTrend == 'up'" class="trend-up">
                                                    ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.netIncomeTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.netIncomeTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.netIncomeVar" class="mb-1 font-size-13">
                                                    {{ comparisonFirmsSanitized.netIncomeVar }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Profit margin
                                            <span class="text-muted mb-0 font-size-11">(%)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.profitMargin" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.profitMargin }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.profitMarginMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.profitMarginMedian
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.profitMarginMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.profitMarginMean
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.profitMarginTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.profitMarginTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.profitMarginTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.profitMarginVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.profitMarginVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                     <tr>
                                        <td>
                                            Revenue per lawyer
                                            <span class="text-muted mb-0 font-size-11">($K)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.revenuePerLawyer" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.revenuePerLawyer }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.revenuePerLawyerMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.revenuePerLawyerMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.revenuePerLawyerMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.revenuePerLawyerMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.revenuePerLawyerTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.revenuePerLawyerTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.revenuePerLawyerTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.revenuePerLawyerVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.revenuePerLawyerVar }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Cost per lawyer
                                            <span class="text-muted mb-0 font-size-11">($K)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.costPerLawyer" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.costPerLawyer }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.costPerLawyerMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.costPerLawyerMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.costPerLawyerMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.costPerLawyerMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.costPerLawyerTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.costPerLawyerTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.costPerLawyerTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.costPerLawyerVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.costPerLawyerVar }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Profits per equity partner
                                            <span class="text-muted mb-0 font-size-11">($K)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.profitPerEquityPartner" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.profitPerEquityPartner }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.profitPerEquityPartnerMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.profitPerEquityPartnerMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.profitPerEquityPartnerMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.profitPerEquityPartnerMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.profitPerEquityPartnerTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.profitPerEquityPartnerTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.profitPerEquityPartnerTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.profitPerEquityPartnerVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.profitPerEquityPartnerVar }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Leverage <span class="text-muted mb-0 font-size-11">(Ratio of all other lawyers to equity partners)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.leverage" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.leverage }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.leverageMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.leverageMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.leverageMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.leverageMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.leverageTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.leverageTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.leverageTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.leverageVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.leverageVar }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            Equity partners as % of total partnership
                                            <span class="text-muted mb-0 font-size-11">(%)</span>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.equityPartnerPercentage" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.equityPartnerPercentage }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.equityPartnerPercentageMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.equityPartnerPercentageMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.equityPartnerPercentageMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.equityPartnerPercentageMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.equityPartnerPercentageTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.equityPartnerPercentageTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>
                                                <p v-else-if="comparisonFirmsSanitized.equityPartnerPercentageTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.equityPartnerPercentageVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.equityPartnerPercentageVar }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr style="border-top:2px solid black;">
                                        <td>
                                            % Female lawyers
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.percentFemale" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.percentFemale }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentFemaleMedian"
                                                    class="mb-1 font-size-13">{{
                                                        comparisonFirmsSanitized.percentFemaleMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentFemaleMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentFemaleMean
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.percentFemaleTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentFemaleTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentFemaleTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentFemaleVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentFemaleVar
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            % Minority lawyers
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.percentMinority" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmSanitized.percentMinority }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentMinorityMedian"
                                                    class="mb-1 font-size-13">{{
                                                        comparisonFirmsSanitized.percentMinorityMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentMinorityMean"
                                                    class="mb-1 font-size-13">{{
                                                        comparisonFirmsSanitized.percentMinorityMean }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.percentMinorityTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentMinorityTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentMinorityTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentMinorityVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentMinorityVar
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            % LGBTQ+ lawyers
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.percentLGBTQ" class="mb-1 font-size-13">{{
                                                    benchmarkFirmSanitized.percentLGBTQ }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentLGBTQMedian"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentLGBTQMedian
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentLGBTQMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentLGBTQMean
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.percentLGBTQTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentLGBTQTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentLGBTQTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentLGBTQVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentLGBTQVar }}
                                                </h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr>
                                        <td>
                                            % Pro bono participation
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmSanitized">
                                                <h6 v-if="benchmarkFirmSanitized.percentProBono" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmSanitized.percentProBono }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>

                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentProBonoMedian"
                                                    class="mb-1 font-size-13">{{
                                                        comparisonFirmsSanitized.percentProBonoMedian }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="comparison">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentProBonoMean"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentProBonoMean
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <p v-if="comparisonFirmsSanitized.percentProBonoTrend == 'up'"
                                                    class="trend-up">ABOVE <i class="fa fa-arrow-up"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentProBonoTrend == 'down'"
                                                    class="trend-down">BELOW <i class="fa fa-arrow-down"></i></p>

                                                <p v-else-if="comparisonFirmsSanitized.percentProBonoTrend == 'neutral'"
                                                    class="trend-neutral">NEUTRAL <i class="fa fa-arrow-right"></i></p>

                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="comparisonFirmsSanitized">
                                                <h6 v-if="comparisonFirmsSanitized.percentProBonoVar"
                                                    class="mb-1 font-size-13">{{ comparisonFirmsSanitized.percentProBonoVar
                                                    }}</h6>
                                                <i v-else class="fa fa-ban"></i>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                </tbody>
                                <!-- end tbody -->
                                </table>
                            </div>
                            <div class="practice-areas-wrapper" v-if="currentTab === 'practiceAreas'">
                                <div v-if="currentTab === 'practiceAreas' && benchmarkFirmProspects" class="column is-4 pl-0 pt-0">
                                    <multiselect @select="fetchFirmProspectsLocations" @remove="dataLocation = '';fetchFirmProspects();" v-model="dataLocation" deselect-label="remove"
                                        placeholder="Select location..." :options="dataLocations"
                                        :searchable="true" :allow-empty="true" >
                                    </multiselect>
                                </div> 
                                <table v-if="currentTab === 'practiceAreas' && benchmarkFirmProspects" class="table is-borderd is-striped pulse-table practice-areas-table">
                                <thead class="table-light practice-areas-table-header">
                                    <tr>
                                        <th >Areas of Practice</th>
                                        <th class="benchmark">{{benchmarkFirmProspects.law360_firm_name}}</th>
                                        <th v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            {{ firm.law360_firm_name }}
                                        </th>
                                    </tr>
                                    <!-- end tr -->
                                </thead>
                                <!-- end thead -->
                                <tbody>
                                    <tr>
                                        <td>
                                            Total Attorneys
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.total_attorneys" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.total_attorneys }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.total_attorneys" class="mb-1 font-size-13">
                                                    {{ firm.total_attorneys }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr style="border-top:2px solid black;">
                                        <td>
                                            Total Partners
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.total_partners" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.total_partners }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.total_partners" class="mb-1 font-size-13">
                                                    {{ firm.total_partners }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Total Counsel
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.total_counsel" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.total_counsel }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.total_counsel" class="mb-1 font-size-13">
                                                    {{ firm.total_counsel }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Total Associates
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.total_associates" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.total_associates }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.total_associates" class="mb-1 font-size-13">
                                                    {{ firm.total_associates }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr style="border-bottom:2px solid black;">
                                        <td>
                                            Total Other
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.total_other" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.total_other }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.total_other" class="mb-1 font-size-13">
                                                    {{ firm.total_other }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showAntitrustDetails = !showAntitrustDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showAntitrustDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Antitrust
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.antitrust" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.antitrust }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.antitrust" class="mb-1 font-size-13">
                                                    {{ firm.antitrust }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showAntitrustDetails" v-for="field in ['antitrust_partner', 'antitrust_counsel', 'antitrust_associate', 'antitrust_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('antitrust_partner', 'Antitrust Partners')
                                                    .replace('antitrust_counsel', 'Antitrust Counsel')
                                                    .replace('antitrust_associate', 'Antitrust Associates')
                                                    .replace('antitrust_other', 'Antitrust Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showBankingDetails = !showBankingDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showBankingDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Banking
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.banking" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.banking }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.banking" class="mb-1 font-size-13">
                                                    {{ firm.banking }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showBankingDetails" v-for="field in ['banking_partner', 'banking_counsel', 'banking_associate', 'banking_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('banking_partner', 'Banking Partners')
                                                    .replace('banking_counsel', 'Banking Counsel')
                                                    .replace('banking_associate', 'Banking Associates')
                                                    .replace('banking_other', 'Banking Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showBankruptcyDetails = !showBankruptcyDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showBankruptcyDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Bankruptcy
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.bankruptcy" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.bankruptcy }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.bankruptcy" class="mb-1 font-size-13">
                                                    {{ firm.bankruptcy }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showBankruptcyDetails" v-for="field in ['bankruptcy_partner', 'bankruptcy_counsel', 'bankruptcy_associate', 'bankruptcy_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('bankruptcy_partner', 'Bankruptcy Partners')
                                                    .replace('bankruptcy_counsel', 'Bankruptcy Counsel')
                                                    .replace('bankruptcy_associate', 'Bankruptcy Associates')
                                                    .replace('bankruptcy_other', 'Bankruptcy Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showCorporateDetails = !showCorporateDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showCorporateDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Corporate
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.corporate" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.corporate }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.corporate" class="mb-1 font-size-13">
                                                    {{ firm.corporate }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showCorporateDetails" v-for="field in ['corporate_partner', 'corporate_counsel', 'corporate_associate', 'corporate_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('corporate_partner', 'Corporate Partners')
                                                    .replace('corporate_counsel', 'Corporate Counsel')
                                                    .replace('corporate_associate', 'Corporate Associates')
                                                    .replace('corporate_other', 'Corporate Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showDataPrivacyDetails = !showDataPrivacyDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showDataPrivacyDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Data Privacy
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.data_privacy" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.data_privacy }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.data_privacy" class="mb-1 font-size-13">
                                                    {{ firm.data_privacy }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showDataPrivacyDetails" v-for="field in ['data_privacy_partner', 'data_privacy_counsel', 'data_privacy_associate', 'data_privacy_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('data_privacy_partner', 'Data Privacy Partners')
                                                    .replace('data_privacy_counsel', 'Data Privacy Counsel')
                                                    .replace('data_privacy_associate', 'Data Privacy Associates')
                                                    .replace('data_privacy_other', 'Data Privacy Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showEnergyDetails = !showEnergyDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showEnergyDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Energy
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.energy" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.energy }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.energy" class="mb-1 font-size-13">
                                                    {{ firm.energy }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showEnergyDetails" v-for="field in ['energy_partner', 'energy_counsel', 'energy_associate', 'energy_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('energy_partner', 'Energy Partners')
                                                    .replace('energy_counsel', 'Energy Counsel')
                                                    .replace('energy_associate', 'Energy Associates')
                                                    .replace('energy_other', 'Energy Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showEntertainmentDetails = !showEntertainmentDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showEntertainmentDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Entertainment
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.entertainment" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.entertainment }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.entertainment" class="mb-1 font-size-13">
                                                    {{ firm.entertainment }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showEntertainmentDetails" v-for="field in ['entertainment_partner', 'entertainment_counsel', 'entertainment_associate', 'entertainment_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('entertainment_partner', 'Entertainment Partners')
                                                    .replace('entertainment_counsel', 'Entertainment Counsel')
                                                    .replace('entertainment_associate', 'Entertainment Associates')
                                                    .replace('entertainment_other', 'Entertainment Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showEnvironmentalDetails = !showEnvironmentalDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showEnvironmentalDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Environmental
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.environmental" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.environmental }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.environmental" class="mb-1 font-size-13">
                                                    {{ firm.environmental }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showEnvironmentalDetails" v-for="field in ['environmental_partner', 'environmental_counsel', 'environmental_associate', 'environmental_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('environmental_partner', 'Environmental Partners')
                                                    .replace('environmental_counsel', 'Environmental Counsel')
                                                    .replace('environmental_associate', 'Environmental Associates')
                                                    .replace('environmental_other', 'Environmental Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showErisaDetails = !showErisaDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showErisaDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            ERISA
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.erisa" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.erisa }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.erisa" class="mb-1 font-size-13">
                                                    {{ firm.erisa }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showErisaDetails" v-for="field in ['erisa_partner', 'erisa_counsel', 'erisa_associate', 'erisa_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('erisa_partner', 'ERISA Partners')
                                                    .replace('erisa_counsel', 'ERISA Counsel')
                                                    .replace('erisa_associate', 'ERISA Associates')
                                                    .replace('erisa_other', 'ERISA Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showFdaDetails = !showFdaDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showFdaDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            FDA
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.fda" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.fda }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.fda" class="mb-1 font-size-13">
                                                    {{ firm.fda }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showFdaDetails" v-for="field in ['fda_partner', 'fda_counsel', 'fda_associate', 'fda_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('fda_partner', 'FDA Partners')
                                                    .replace('fda_counsel', 'FDA Counsel')
                                                    .replace('fda_associate', 'FDA Associates')
                                                    .replace('fda_other', 'FDA Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showGovernmentDetails = !showGovernmentDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showGovernmentDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Government
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.government" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.government }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.government" class="mb-1 font-size-13">
                                                    {{ firm.government }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showGovernmentDetails" v-for="field in ['government_partner', 'government_counsel', 'government_associate', 'government_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('government_partner', 'Government Partners')
                                                    .replace('government_counsel', 'Government Counsel')
                                                    .replace('government_associate', 'Government Associates')
                                                    .replace('government_other', 'Government Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showHealthCareDetails = !showHealthCareDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showHealthCareDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Health Care
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.health_care" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.health_care }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.health_care" class="mb-1 font-size-13">
                                                    {{ firm.health_care }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showHealthCareDetails" v-for="field in ['health_care_partner', 'health_care_counsel', 'health_care_associate', 'health_care_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('health_care_partner', 'Health Care Partners')
                                                    .replace('health_care_counsel', 'Health Care Counsel')
                                                    .replace('health_care_associate', 'Health Care Associates')
                                                    .replace('health_care_other', 'Health Care Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showInsuranceDetails = !showInsuranceDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showInsuranceDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Insurance
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.insurance" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.insurance }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.insurance" class="mb-1 font-size-13">
                                                    {{ firm.insurance }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showInsuranceDetails" v-for="field in ['insurance_partner', 'insurance_counsel', 'insurance_associate', 'insurance_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('insurance_partner', 'Insurance Partners')
                                                    .replace('insurance_counsel', 'Insurance Counsel')
                                                    .replace('insurance_associate', 'Insurance Associates')
                                                    .replace('insurance_other', 'Insurance Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showIntellectualPropertyDetails = !showIntellectualPropertyDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showIntellectualPropertyDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Intellectual Property
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.intellectual_property" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.intellectual_property }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.intellectual_property" class="mb-1 font-size-13">
                                                    {{ firm.intellectual_property }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showIntellectualPropertyDetails" v-for="field in ['intellectual_property_partner', 'intellectual_property_counsel', 'intellectual_property_associate', 'intellectual_property_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('intellectual_property_partner', 'Intellectual Property Partners')
                                                    .replace('intellectual_property_counsel', 'Intellectual Property Counsel')
                                                    .replace('intellectual_property_associate', 'Intellectual Property Associates')
                                                    .replace('intellectual_property_other', 'Intellectual Property Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showInternationalDetails = !showInternationalDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showInternationalDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            International
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.international" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.international }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.international" class="mb-1 font-size-13">
                                                    {{ firm.international }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showInternationalDetails" v-for="field in ['international_partner', 'international_counsel', 'international_associate', 'international_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('international_partner', 'International Partners')
                                                    .replace('international_counsel', 'International Counsel')
                                                    .replace('international_associate', 'International Associates')
                                                    .replace('international_other', 'International Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showLaborEmploymentDetails = !showLaborEmploymentDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showLaborEmploymentDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Labor & Employment
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.labor__employment" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.labor__employment }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.labor__employment" class="mb-1 font-size-13">
                                                    {{ firm.labor__employment }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showLaborEmploymentDetails" v-for="field in ['labor__employment_partner', 'labor__employment_counsel', 'labor__employment_associate', 'labor__employment_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('labor__employment_partner', 'Labor & Employment Partners')
                                                    .replace('labor__employment_counsel', 'Labor & Employment Counsel')
                                                    .replace('labor__employment_associate', 'Labor & Employment Associates')
                                                    .replace('labor__employment_other', 'Labor & Employment Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showLitigationDetails = !showLitigationDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showLitigationDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Litigation
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.litigation" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.litigation }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.litigation" class="mb-1 font-size-13">
                                                    {{ firm.litigation }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showLitigationDetails" v-for="field in ['litigation_partner', 'litigation_counsel', 'litigation_associate', 'litigation_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('litigation_partner', 'Litigation Partners')
                                                    .replace('litigation_counsel', 'Litigation Counsel')
                                                    .replace('litigation_associate', 'Litigation Associates')
                                                    .replace('litigation_other', 'Litigation Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showMediaDetails = !showMediaDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showMediaDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Media
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.media" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.media }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.media" class="mb-1 font-size-13">
                                                    {{ firm.media }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showMediaDetails" v-for="field in ['media_partner', 'media_counsel', 'media_associate', 'media_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('media_partner', 'Media Partners')
                                                    .replace('media_counsel', 'Media Counsel')
                                                    .replace('media_associate', 'Media Associates')
                                                    .replace('media_other', 'Media Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showRealEstateDetails = !showRealEstateDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showRealEstateDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Real Estate
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.real_estate" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.real_estate }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.real_estate" class="mb-1 font-size-13">
                                                    {{ firm.real_estate }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showRealEstateDetails" v-for="field in ['real_estate_partner', 'real_estate_counsel', 'real_estate_associate', 'real_estate_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('real_estate_partner', 'Real Estate Partners')
                                                    .replace('real_estate_counsel', 'Real Estate Counsel')
                                                    .replace('real_estate_associate', 'Real Estate Associates')
                                                    .replace('real_estate_other', 'Real Estate Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showTaxDetails = !showTaxDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showTaxDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Tax
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.tax" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.tax }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.tax" class="mb-1 font-size-13">
                                                    {{ firm.tax }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showTaxDetails" v-for="field in ['tax_partner', 'tax_counsel', 'tax_associate', 'tax_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('tax_partner', 'Tax Partners')
                                                    .replace('tax_counsel', 'Tax Counsel')
                                                    .replace('tax_associate', 'Tax Associates')
                                                    .replace('tax_other', 'Tax Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showTelecommunicationsDetails = !showTelecommunicationsDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showTelecommunicationsDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Telecommunications
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.telecommunications" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.telecommunications }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.telecommunications" class="mb-1 font-size-13">
                                                    {{ firm.telecommunications }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showTelecommunicationsDetails" v-for="field in ['telecommunications_partner', 'telecommunications_counsel', 'telecommunications_associate', 'telecommunications_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('telecommunications_partner', 'Telecommunications Partners')
                                                    .replace('telecommunications_counsel', 'Telecommunications Counsel')
                                                    .replace('telecommunications_associate', 'Telecommunications Associates')
                                                    .replace('telecommunications_other', 'Telecommunications Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showTransportationDetails = !showTransportationDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showTransportationDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Transportation
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.transportation" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.transportation }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.transportation" class="mb-1 font-size-13">
                                                    {{ firm.transportation }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showTransportationDetails" v-for="field in ['transportation_partner', 'transportation_counsel', 'transportation_associate', 'transportation_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('transportation_partner', 'Transportation Partners')
                                                    .replace('transportation_counsel', 'Transportation Counsel')
                                                    .replace('transportation_associate', 'Transportation Associates')
                                                    .replace('transportation_other', 'Transportation Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr @click="showTrustsEstatesDetails = !showTrustsEstatesDetails" style="cursor:pointer;">
                                        <td>
                                            <span  v-if="showTrustsEstatesDetails" class="caret-down"><i class="fa fa-caret-down"></i></span>
                                            <span v-else class="caret-right"><i class="fa fa-caret-right"></i></span>
                                            Trusts & Estates
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects.trusts__estates" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects.trusts__estates }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm.trusts__estates" class="mb-1 font-size-13">
                                                    {{ firm.trusts__estates }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="showTrustsEstatesDetails" v-for="field in ['trusts__estates_partner', 'trusts__estates_counsel', 'trusts__estates_associate', 'trusts__estates_other']" :key="field">
                                        <td>
                                            &nbsp;&nbsp;{{ field.replace('trusts__estates_partner', 'Trusts & Estates Partners')
                                                    .replace('trusts__estates_counsel', 'Trusts & Estates Counsel')
                                                    .replace('trusts__estates_associate', 'Trusts & Estates Associates')
                                                    .replace('trusts__estates_other', 'Trusts & Estates Other') }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmProspects">
                                                <h6 v-if="benchmarkFirmProspects[field]" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmProspects[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmProspects" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <h6 v-if="firm[field]" class="mb-1 font-size-13">
                                                    {{ firm[field] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                    </tr>
                                </tbody>
                                <!-- end tbody -->
                            </table>
                            </div> 
                            <!-- end table -->
                            <div class="locations-wrapper" v-if="currentTab === 'locations'">
                                <div v-if="currentTab === 'locations' && benchmarkFirmFirmLevel" class="column is-4 pl-0 pt-0">
                                    <multiselect v-model="selectedPracticeArea"
                                        track-by="value" label="name" placeholder="Select a practice area..." :options="practiceAreasList"
                                        :searchable="true" >
                                        <template v-slot:singleLabel="{ option }">{{ option.name }}</template>
                                    </multiselect>
                                </div>
                                <table v-if="currentTab === 'locations' && benchmarkFirmFirmLevel" class="table is-borderd is-striped pulse-table practice-areas-table">
                                <thead class="table-light locations-table-header">
                                    <tr>
                                        <th >Location</th>
                                        <th class="benchmark">{{benchmarkFirmFirmLevel.law360_firm_name}}</th>
                                        <th v-for="(firm, idx) in comparisonFirmsFirmLevel" :key="firm.editorial_id || idx" class="comparison">
                                            {{ firm.law360_firm_name }}
                                        </th>
                                    </tr>
                                    <!-- end tr -->
                                </thead>
                                <!-- end thead -->
                                <tbody>
                                    <tr>
                                        <td>
                                            Total Attorneys
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmFirmLevel">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            benchmarkFirmAllLocations
                                                                ? benchmarkFirmAllLocations
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj[selectedPracticeArea.value]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 v-if="benchmarkFirmFirmLevel.total_attorneys" class="mb-1 font-size-13">
                                                        {{ benchmarkFirmFirmLevel.total_attorneys }}
                                                    </h6>
                                                    <h6 v-else>0</h6>
                                                </template>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmsFirmLevel" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj[selectedPracticeArea.value]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj.total_attorneys);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr style="border-top:2px solid black;">
                                        <td>
                                            Total Partners
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmFirmLevel">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            benchmarkFirmAllLocations
                                                                ? benchmarkFirmAllLocations
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_partner';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 v-if="benchmarkFirmFirmLevel.total_partners" class="mb-1 font-size-13">
                                                        {{ benchmarkFirmFirmLevel.total_partners }}
                                                    </h6>
                                                    <h6 v-else>0</h6>
                                                </template>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmsFirmLevel" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_partner';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj.total_partners);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                            </template>
                                        </td>                                   
                                    </tr>
                                    <tr>
                                        <td>
                                            Total Counsel
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmFirmLevel">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            benchmarkFirmAllLocations
                                                                ? benchmarkFirmAllLocations
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_counsel';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 v-if="benchmarkFirmFirmLevel.total_counsel" class="mb-1 font-size-13">
                                                        {{ benchmarkFirmFirmLevel.total_counsel }}
                                                    </h6>
                                                    <h6 v-else>0</h6>
                                                </template>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmsFirmLevel" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_counsel';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj.total_counsel);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Total Associates
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmFirmLevel">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            benchmarkFirmAllLocations
                                                                ? benchmarkFirmAllLocations
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_associate';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 v-if="benchmarkFirmFirmLevel.total_associates" class="mb-1 font-size-13">
                                                        {{ benchmarkFirmFirmLevel.total_associates }}
                                                    </h6>
                                                    <h6 v-else>0</h6>
                                                </template>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmsFirmLevel" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_associate';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj.total_associates);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr style="border-bottom:2px solid black;">
                                        <td>
                                            Total Other
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmFirmLevel">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            benchmarkFirmAllLocations
                                                                ? benchmarkFirmAllLocations
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_other';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 v-if="benchmarkFirmFirmLevel.total_other" class="mb-1 font-size-13">
                                                        {{ benchmarkFirmFirmLevel.total_other }}
                                                    </h6>
                                                    <h6 v-else>0</h6>
                                                </template>
                                            </template>
                                        </td>
                                        <td v-for="(firm, idx) in comparisonFirmsFirmLevel" :key="firm.editorial_id || idx" class="comparison">
                                            <template v-if="firm">
                                                <template v-if="selectedPracticeArea">
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const key = selectedPracticeArea.value + '_other';
                                                                        const val = Number(locObj[key]);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                                <template v-else>
                                                    <h6 class="mb-1 font-size-13">
                                                        {{
                                                            comparisonFirmsAllLocations && comparisonFirmsAllLocations[idx]
                                                                ? comparisonFirmsAllLocations[idx]
                                                                    .filter(locObj => dataLocations.includes(locObj.office_location) && locObj.office_location !== "")
                                                                    .reduce((sum, locObj) => {
                                                                        const val = Number(locObj.total_other);
                                                                        return sum + (isNaN(val) ? 0 : val);
                                                                    }, 0)
                                                                : 0
                                                        }}
                                                    </h6>
                                                </template>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                    <tr v-for="location in dataLocations.filter(loc => loc !== '')" :key="location">
                                        <td>
                                            {{ location }}
                                        </td>
                                        <td class="benchmark">
                                            <template v-if="benchmarkFirmAllLocations">
                                                <h6 v-if="benchmarkFirmAllLocations.find(locObj => locObj.office_location === location)?.[selectedPracticeArea ? selectedPracticeArea.value : 'total_attorneys'] !== undefined" class="mb-1 font-size-13">
                                                    {{ benchmarkFirmAllLocations.find(locObj => locObj.office_location === location)?.[selectedPracticeArea ? selectedPracticeArea.value : 'total_attorneys'] }}
                                                </h6>
                                                <h6 v-else>0</h6>
                                            </template>
                                        </td>
                                        <td v-for="(firmLocations, idx) in comparisonFirmsAllLocations" :key="idx" class="comparison">
                                            <template v-if="firmLocations">
                                                <h6 v-if="firmLocations.find(locObj => locObj.office_location === location)?.[selectedPracticeArea ? selectedPracticeArea.value : 'total_attorneys'] !== undefined" class="mb-1 font-size-13">
                                                    {{ firmLocations.find(locObj => locObj.office_location === location)?.[selectedPracticeArea ? selectedPracticeArea.value : 'total_attorneys'] }}
                                                </h6>
                                                <h6 v-else>-</h6>
                                            </template>
                                        </td>
                                    </tr>
                                    <!-- end /tr -->
                                </tbody>
                                <!-- end tbody -->
                            </table>
                            </div>
                            <div v-if="currentTab === 'overview'" class="pulse-table-key">
                                <span class="unavailable"><i class="fa fa-ban"></i> No Data Available</span>
                            </div>
                            <div v-if="currentTab === 'locations'" class="pulse-table-key">
                                <span class="dash"><i>-</i>&nbsp;No Office</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="benchmarkFirmData && currentTab === 'overview'" >
                    <div class="columns is-multiline is-12 overview-charts">
                        <!-- Gross revenue -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                        chart: {
                                            type: 'bar',
                                            toolbar: {
                                                show: false,
                                                tools: {
                                                    download: false
                                                }
                                            },
                                        },
                                        plotOptions: {
                                            bar: {
                                                horizontal: true,
                                            },
                                        },
                                        dataLabels: {
                                            style: {
                                                colors: ['#000000'],
                                                fontWeight: 'normal',
                                            }
                                        },
                                        xaxis: {
                                            categories: chartNames,
                                        },
                                        fill: {
                                            opacity: 1,
                                        },
                                        colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                        states: {
                                            hover: {
                                                filter: {
                                                    type: 'darken',
                                                    value: 0.1,
                                                }
                                            },
                                        },
                                        title: {
                                            text: 'Gross revenue millions of dollars',
                                            align: 'left',
                                        },
                                    }" :series="[
                                        {
                                            name: 'Gross revenue millions of dollars',
                                            data: grossRevenueChartData,
                                        },
                                    ]"></apexchart>
                            </div>
                        </div>
                        <!-- Revenue Cahnge -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Gross revenue change vs. prior year %',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    },
                                }" :series="[
                                    {
                                        name: 'Gross revenue change vs. prior year %',
                                        data: revenueChangeChartData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                        <!-- Net income millons of dollars -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Net income millons of dollars',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    }
                                }" :series="[
                                    {
                                        name: 'Net income millons of dollars',
                                        data: netIncomeChartData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                        <!-- Profit Margin -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Profit margin %',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    }
                                }" :series="[
                                    {
                                        name: 'Profit margin %',
                                        data: profitMarginChartData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                        <!-- Proportion of female lawyers % -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Proportion of female lawyers %',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    }
                                }" :series="[
                                    {
                                        name: 'Proportion of female lawyers %',
                                        data: portionFemaleData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                        <!-- Proportion of minority lawyers % -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Proportion of minority lawyers %',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    },
                                }" :series="[
                                    {
                                        name: 'Proportion of minority lawyers %',
                                        data: portionMinortyData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                        <!-- Proportion of LGBTQ lawyers % -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Proportion of LGBTQ+ lawyers %',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    },
                                }" :series="[
                                    {
                                        name: 'Proportion of LGBTQ+ lawyers %',
                                        data: portionLGBTQData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                        <!-- Proportion of ProBono lawyers % -->
                        <div class="column is-12">
                            <div class="p-5 has-background-white">
                                <apexchart height="400" type="bar" :options="{
                                    chart: {
                                        type: 'bar',
                                        toolbar: {
                                            show: false,
                                            tools: {
                                                download: false
                                            }
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: true,
                                        },
                                    },
                                    xaxis: {
                                        categories: chartNames,
                                    },
                                    fill: {
                                        opacity: 1,
                                    },
                                    colors: ['rgba(215, 230, 241, 0.85)', 'rgb(0, 103, 177)'],
                                    states: {
                                        hover: {
                                            filter: {
                                                type: 'darken',
                                                value: 0.1,
                                            }
                                        },
                                    },
                                    title: {
                                        text: 'Proportion of pro bono participation %',
                                        align: 'left',
                                    },
                                    dataLabels: {
                                        position: 'top',
                                        style: {
                                            colors: ['#000000'],
                                            fontWeight: 'normal',
                                        }
                                    }
                                }" :series="[
                                    {
                                        name: 'Proportion of pro bono participation %',
                                        data: portionProBonoData,
                                    },
                                ]"></apexchart>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal" :class="{ 'is-active': showConfirm }">
            <div class="modal-background" @click="toggleSaveModal"></div>
            <div class="modal-card">
                <header class="modal-card-head has-background-white">
                    <p class="modal-card-title is-size-5">Save Your Selection</p>
                    <button class="delete" aria-label="close" @click="toggleSaveModal"></button>
                </header>
                <section class="modal-card-body">
                    <label for="benchmark-name" class="is-size-6 pb-3">Name: <span
                            class="has-text-grey-light is-size-6">(Required)</span></label>
                    <input class="input" id="benchmark-name" type="text" v-model="benchmarkName"
                        placeholder="Enter a meaningful name">
                </section>
                <footer class="modal-card-foot has-background-white is-justify-content-center">
                    <button @click="saveBenchmark" class="button is-success">Save changes</button>
                    <button @click="toggleSaveModal" class="button">Cancel</button>
                </footer>
            </div>
        </div>
        <div class="modal" :class="{ 'is-active': showInfo }">
            <div class="modal-background" @click="toggleInfoModal"></div>
            <div class="modal-card">
                <header class="modal-card-head has-background-white">
                    <p class="modal-card-title is-size-5">Save Your Selection</p>
                    <button class="delete" aria-label="close" @click="toggleInfoModal"></button>
                </header>
                <section class="modal-card-body">
                    <h3 class="is-size-5 has-text-weight-bold mb-2">
                        Median Average:
                    </h3>
                    <p class="is-size-6 mb-4">
                        The middle value in the dataset for the comparison firms
                    </p>
                    <h3 class="is-size-5 has-text-weight-bold mb-2">
                        Mean Average:
                    </h3>
                    <p class="is-size-6 mb-4">
                        The average value of all of the comparison firms
                    </p>
                    <h3 class="is-size-5 has-text-weight-bold mb-2">
                        Trend vs Mean Average:
                    </h3>
                    <p class="is-size-6 mb-4">
                        Directional trend (higher, lower, same) of the benchmark firm vs. the mean average of the comparison group
                    </p>
                    <h3 class="is-size-5 has-text-weight-bold mb-2">
                        Variance vs Mean Average:
                    </h3>
                    <p class="is-size-6 mb-4">
                        Numerical variance of the benchmark firm vs. the mean average of the comparison group
                    </p>
                </section>
                 <footer style="border:none;" class="modal-card-foot has-background-white is-justify-content-center">
                </footer>
            </div>
        </div>
        <div class="modal" :class="{ 'is-active': showTeaser }">
            <div class="modal-background" ></div>
            <div class="modal-card">
                <section class="modal-card-body modal-teaser p-6">
                    <div class="subscription-box">
                    <h2 class="has-text-centered is-size-3 my-2">Want to continue reading?</h2>
                    <h2><a href="/pulse/subscribe/free_trial?return_url=%2Fpulse%2Fleaderboard-analytics"  class="btn btn-danger btn-block btn-lg has-text-white">Take a 7 Day FREE Trial</a></h2>
                    <p class="is-size-6 my-3">Unlock these <strong>benefits</strong> today when you sign-up for a FREE 7-day trial:</p>
                    <ul style="text-align:left">
                        <li class="is-size-6 my-2" style="list-style-type:disc">Gain a <strong>competitive edge</strong> with <strong>exclusive data visualization tools</strong> to tailor to your practice</li>
                        <li class="is-size-6 my-2" style="list-style-type:disc"><strong>Stay informed</strong> with <strong>daily newsletters and custom alerts</strong> across 14+ coverage areas relevant to you</li>
                        <li class="is-size-6 my-2" style="list-style-type:disc"><strong>Streamline your business of law needs</strong> with integrated news and research in a <strong>single destination</strong></li>
                    </ul>
                    <p class="has-text-centered is-size-5 my-3"><strong>Already have an account?</strong> <a href="/pulse/account/login?return_url=%2Fpulse%2Fleaderboard-analytics" style="text-decoration: underline;">Sign In Now</a></p>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
// Configure Axios to include cookies
axios.defaults.withCredentials = true;
// Get the CSRF token from the HTML
const csrfToken = document.querySelector('meta[name=csrf-token]').content;
// Configure Axios with the CSRF token in the headers
axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
const PRINT_ELEMENT_ID = 'print-section';
const PRINT_WINDOW_FEATURES = 'fullscreen=yes,titlebar=yes,scrollbars=yes';

// Opens the report in a new window and prints it; the window is left open afterwards.
// (Local replacement for the removed `paperizer` package.)
function paperize(stylesheets) {
    const element = window.document.getElementById(PRINT_ELEMENT_ID);
    if (!element) {
        throw new Error(`Element with id '${PRINT_ELEMENT_ID}' was not found.`);
    }

    const printWindow = window.open('', '_blank', PRINT_WINDOW_FEATURES);
    if (!printWindow) return;
    if (!printWindow.opener) printWindow.opener = self;
    printWindow.focus();

    printWindow.document.title = document.title;
    printWindow.document.write(`
      <html>
        <head>
          <title>${printWindow.document.title}</title>
        </head>
        <body class="">
          ${element.innerHTML}
        </body>
      </html>
    `);

    stylesheets.forEach((href) => {
        const link = printWindow.document.createElement('link');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('type', 'text/css');
        link.setAttribute('href', href);
        printWindow.document.getElementsByTagName('head')[0].appendChild(link);
    });

    // Give the injected stylesheets time to load before the print dialog samples the layout.
    setTimeout(() => {
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    }, 1000);
}

import Multiselect from 'vue-multiselect';
export default {
    components: {
        'multiselect': Multiselect
    },
    data: function () {
        return {
            allLawFirms: [],
            benchmarkFirm: null,
            comparisonFirms: [],
            benchmarkFirmData: null,
            comparsionFirmData: null,
            benchmarkFirmProspects: null,
            comparisonFirmProspects: null,
            benchmarkFirmFirmLevel: null,
            benchmarkFirmAllLocations: null,
            comparisonFirmsFirmLevel: null,
            comparisonFirmsAllLocations: null,
            allFirmData: null,
            savedFirms: [],
            showConfirm: false,
            showInfo: false,
            benchmarkName: "",
            savedBenchmarks: [],
            selectedSavedBenchmark: null,
            showTeaser: false,
            showSaveSuccess: false,
            dataYears: ['2022', '2023', '2024'],
            dataYear: '2024',
            dataLocations: [''],
            dataLocation: '',
            currentTab: 'overview',
            // practice area detail toggles
            showAntitrustDetails: false,
            showBankingDetails: false,
            showBankruptcyDetails: false,
            showCorporateDetails: false,
            showDataPrivacyDetails: false,
            showEnergyDetails: false,
            showEntertainmentDetails: false,
            showEnvironmentalDetails: false,
            showErisaDetails: false,
            showFdaDetails: false,
            showGovernmentDetails: false,
            showHealthCareDetails: false,
            showInsuranceDetails: false,
            showIntellectualPropertyDetails: false,
            showInternationalDetails: false,
            showLaborEmploymentDetails: false,
            showLitigationDetails: false,
            showMediaDetails: false,
            showRealEstateDetails: false,
            showTaxDetails: false,
            showTelecommunicationsDetails: false,
            showTransportationDetails: false,
            showTrustsEstatesDetails: false,
            selectedPracticeArea: null,
            isSubscriber: false,
            practiceAreasList: [
                { name: "Antitrust", value: "antitrust" },
                // { name: "Antitrust Associates", value: "antitrust_associate" },
                // { name: "Antitrust Counsel", value: "antitrust_counsel" },
                // { name: "Antitrust Other", value: "antitrust_other" },
                // { name: "Antitrust Partners", value: "antitrust_partner" },
                { name: "Banking", value: "banking" },
                // { name: "Banking Associates", value: "banking_associate" },
                // { name: "Banking Counsel", value: "banking_counsel" },
                // { name: "Banking Other", value: "banking_other" },
                // { name: "Banking Partners", value: "banking_partner" },
                { name: "Bankruptcy", value: "bankruptcy" },
                // { name: "Bankruptcy Associates", value: "bankruptcy_associate" },
                // { name: "Bankruptcy Counsel", value: "bankruptcy_counsel" },
                // { name: "Bankruptcy Other", value: "bankruptcy_other" },
                // { name: "Bankruptcy Partners", value: "bankruptcy_partner" },
                { name: "Corporate", value: "corporate" },
                // { name: "Corporate Associates", value: "corporate_associate" },
                // { name: "Corporate Counsel", value: "corporate_counsel" },
                // { name: "Corporate Other", value: "corporate_other" },
                // { name: "Corporate Partners", value: "corporate_partner" },
                { name: "Data Privacy", value: "data_privacy" },
                // { name: "Data Privacy Associates", value: "data_privacy_associate" },
                // { name: "Data Privacy Counsel", value: "data_privacy_counsel" },
                // { name: "Data Privacy Other", value: "data_privacy_other" },
                // { name: "Data Privacy Partners", value: "data_privacy_partner" },
                { name: "Energy", value: "energy" },
                // { name: "Energy Associates", value: "energy_associate" },
                // { name: "Energy Counsel", value: "energy_counsel" },
                // { name: "Energy Other", value: "energy_other" },
                // { name: "Energy Partners", value: "energy_partner" },
                { name: "Entertainment", value: "entertainment" },
                // { name: "Entertainment Associates", value: "entertainment_associate" },
                // { name: "Entertainment Counsel", value: "entertainment_counsel" },
                // { name: "Entertainment Other", value: "entertainment_other" },
                // { name: "Entertainment Partners", value: "entertainment_partner" },
                { name: "Environmental", value: "environmental" },
                // { name: "Environmental Associates", value: "environmental_associate" },
                // { name: "Environmental Counsel", value: "environmental_counsel" },
                // { name: "Environmental Other", value: "environmental_other" },
                // { name: "Environmental Partners", value: "environmental_partner" },
                { name: "Erisa", value: "erisa" },
                // { name: "Erisa Associates", value: "erisa_associate" },
                // { name: "Erisa Counsel", value: "erisa_counsel" },
                // { name: "Erisa Other", value: "erisa_other" },
                // { name: "Erisa Partners", value: "erisa_partner" },
                { name: "FDA", value: "fda" },
                // { name: "FDA Associates", value: "fda_associate" },
                // { name: "FDA Counsel", value: "fda_counsel" },
                // { name: "FDA Other", value: "fda_other" },
                // { name: "FDA Partners", value: "fda_partner" },
                { name: "Government", value: "government" },
                // { name: "Government Associates", value: "government_associate" },
                // { name: "Government Counsel", value: "government_counsel" },
                // { name: "Government Other", value: "government_other" },
                // { name: "Government Partners", value: "government_partner" },
                { name: "Health Care", value: "health_care" },
                // { name: "Health Care Associates", value: "health_care_associate" },
                // { name: "Health Care Counsel", value: "health_care_counsel" },
                // { name: "Health Care Other", value: "health_care_other" },
                // { name: "Health Care Partners", value: "health_care_partner" },
                { name: "Insurance", value: "insurance" },
                // { name: "Insurance Associates", value: "insurance_associate" },
                // { name: "Insurance Counsel", value: "insurance_counsel" },
                // { name: "Insurance Other", value: "insurance_other" },
                // { name: "Insurance Partners", value: "insurance_partner" },
                { name: "Intellectual Property", value: "intellectual_property" },
                // { name: "Intellectual Property Associates", value: "intellectual_property_associate" },
                // { name: "Intellectual Property Counsel", value: "intellectual_property_counsel" },
                // { name: "Intellectual Property Other", value: "intellectual_property_other" },
                // { name: "Intellectual Property Partners", value: "intellectual_property_partner" },
                { name: "International", value: "international" },
                // { name: "International Associates", value: "international_associate" },
                // { name: "International Counsel", value: "international_counsel" },
                // { name: "International Other", value: "international_other" },
                // { name: "International Partners", value: "international_partner" },
                { name: "Labor & Employment", value: "labor__employment" },
                // { name: "Labor & Employment Associates", value: "labor__employment_associate" },
                // { name: "Labor & Employment Counsel", value: "labor__employment_counsel" },
                // { name: "Labor & Employment Other", value: "labor__employment_other" },
                // { name: "Labor & Employment Partners", value: "labor__employment_partner" },
                { name: "Litigation", value: "litigation" },
                // { name: "Litigation Associates", value: "litigation_associate" },
                // { name: "Litigation Counsel", value: "litigation_counsel" },
                // { name: "Litigation Other", value: "litigation_other" },
                // { name: "Litigation Partners", value: "litigation_partner" },
                { name: "Media", value: "media" },
                // { name: "Media Associates", value: "media_associate" },
                // { name: "Media Counsel", value: "media_counsel" },
                // { name: "Media Other", value: "media_other" },
                // { name: "Media Partners", value: "media_partner" },
                { name: "Real Estate", value: "real_estate" },
                // { name: "Real Estate Associates", value: "real_estate_associate" },
                // { name: "Real Estate Counsel", value: "real_estate_counsel" },
                // { name: "Real Estate Other", value: "real_estate_other" },
                // { name: "Real Estate Partners", value: "real_estate_partner" },
                { name: "Tax", value: "tax" },
                // { name: "Tax Associates", value: "tax_associate" },
                // { name: "Tax Counsel", value: "tax_counsel" },
                // { name: "Tax Other", value: "tax_other" },
                // { name: "Tax Partners", value: "tax_partner" },
                { name: "Telecommunications", value: "telecommunications" },
                // { name: "Telecommunications Associates", value: "telecommunications_associate" },
                // { name: "Telecommunications Counsel", value: "telecommunications_counsel" },
                // { name: "Telecommunications Other", value: "telecommunications_other" },
                // { name: "Telecommunications Partners", value: "telecommunications_partner" },
                { name: "Transportation", value: "transportation" },
                // { name: "Transportation Associates", value: "transportation_associate" },
                // { name: "Transportation Counsel", value: "transportation_counsel" },
                // { name: "Transportation Other", value: "transportation_other" },
                // { name: "Transportation Partners", value: "transportation_partner" },
                { name: "Trusts & Estates", value: "trusts__estates" },
                // { name: "Trusts & Estates Associates", value: "trusts__estates_associate" },
                // { name: "Trusts & Estates Counsel", value: "trusts__estates_counsel" },
                // { name: "Trusts & Estates Other", value: "trusts__estates_other" },
                // { name: "Trusts & Estates Partners", value: "trusts__estates_partner" }
            ]
        }
    },
    methods: {
        fetchAllData: function () {
            this.dataLocation = '';
            this.selectedPracticeArea = null;
            this.fetchBenchmark();
            this.fetchFirmProspects();
            this.fetchFirmProspectsLocationsData();
        },
        fetchBenchmark: function () {
            const comparisonUrl = `${this.ecosystemPrefix}/benchmarking/compare`;
            const benchmarkId = this.benchmarkFirm.editorial_id;
            let comparisonFirms = ""
            this.comparisonFirms.forEach((firm) => {
                comparisonFirms += "&benchmark[comparison_id][]=" + firm.editorial_id
            })
            axios.get(`${comparisonUrl}?benchmark[benchmark_id]=${benchmarkId}${comparisonFirms}&benchmark[year]=${this.dataYear}`)
                .then((response) => {
                    this.benchmarkFirmData = response.data.benchmark_firm;
                    this.comparsionFirmData = response.data.comparison_aggregate
                    this.allFirmData = response.data.comparison_firms
                })
        },
        fetchFirmProspects: function () {
            const benchmarkId = this.benchmarkFirm.editorial_id;
            const comparisonFirms = this.comparisonFirms.map(firm => `&firm_prospects[comparison_id][]=${firm.editorial_id}`).join('');
            const year = this.dataYear;
            const location = ''; // blank for all locations

            // Fetch firm prospects data
            axios.get(`${this.ecosystemPrefix}/benchmarking/firm_prospects_data?firm_prospects[benchmark_id]=${benchmarkId}${comparisonFirms}&firm_prospects[year]=${year}&firm_prospects[location]=${location}`)
                .then((response) => {
                    this.benchmarkFirmProspects = response.data.benchmark_firm;
                    this.comparisonFirmProspects = response.data.comparison_firms;
                });

            // Fetch available locations
            axios.get(`${this.ecosystemPrefix}/benchmarking/firm_prospects_locations?firm_prospects[benchmark_id]=${benchmarkId}${comparisonFirms}&firm_prospects[year]=${year}&firm_prospects[location]=${location}}`)
                .then((response) => {
                    this.dataLocations = ['', ...response.data.filter(loc => loc !== "")];
                    // If the current location is not in the new list, reset it
                    if (!this.dataLocations.includes(this.dataLocation)) {
                        this.dataLocation = '';
                    }
                });
        },
        fetchFirmProspectsLocationsData: function () {
            const benchmarkId = this.benchmarkFirm.editorial_id;
            const comparisonFirms = this.comparisonFirms.map(firm => `&firm_prospects[comparison_id][]=${firm.editorial_id}`).join('');
            const year = this.dataYear;
            const location = ''; // blank for all locations

            // Fetch firm prospects data
            axios.get(`${this.ecosystemPrefix}/benchmarking/firm_prospects_locations_data?firm_prospects[benchmark_id]=${benchmarkId}${comparisonFirms}&firm_prospects[year]=${year}&firm_prospects[location]=${location}`)
                .then((response) => {
                    this.benchmarkFirmFirmLevel = response.data.benchmark_firm_firm_level;
                    this.benchmarkFirmAllLocations = response.data.benchmark_firm_all_locations;
                    this.comparisonFirmsFirmLevel = response.data.comparison_firms_firm_level;
                    this.comparisonFirmsAllLocations = response.data.comparison_firms_all_locations;
                });
        },
        fetchFirmProspectsLocations: function () {
            const benchmarkId = this.benchmarkFirm.editorial_id;
            const comparisonFirms = this.comparisonFirms.map(firm => `&firm_prospects[comparison_id][]=${firm.editorial_id}`).join('');
            const year = this.dataYear;
            const location = this.dataLocation; // use selected location


            // Fetch firm prospects data for the selected location
            axios.get(`${this.ecosystemPrefix}/benchmarking/firm_prospects_data?firm_prospects[benchmark_id]=${benchmarkId}${comparisonFirms}&firm_prospects[year]=${year}&firm_prospects[location]=${location}`)
                .then((response) => {
                    this.benchmarkFirmProspects = response.data.benchmark_firm;
                    this.comparisonFirmProspects = response.data.comparison_firms;
                });
        },
        fetchBenchmarkCsv: function () {
            const comparisonUrl = `${this.ecosystemPrefix}/benchmarking/compare`;
            const benchmarkId = this.benchmarkFirm.editorial_id;
            let comparisonFirms = ""
            this.comparisonFirms.forEach((firm) => {
                comparisonFirms += "&benchmark[comparison_id][]=" + firm.editorial_id
            })
            axios.get(`${comparisonUrl}?csv=true&benchmark[benchmark_id]=${benchmarkId}${comparisonFirms}&benchmark[year]=${this.dataYear}`, {
                responseType: "blob", // Set the response type to 'blob' to handle binary data
            })
            .then((response) => {
                // Create a Blob from the response data
                const blob = new Blob([response.data], { type: "text/csv" });

                // Create a link element and trigger a click to download the file
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = "Law360-Pulse-Leaderboard-Analytics.csv";
                link.click();

                // Cleanup: remove the link and revoke the object URL
                window.URL.revokeObjectURL(link.href);
            })
        },
        fetchSavedBenchmark: function (selectedOption, id) {
            this.benchmarkFirm = selectedOption.benchmark_firm;
            this.comparisonFirms = selectedOption.comparison_firms;
            this.dataYear = selectedOption.year;
            this.dataLocation = '';
            this.fetchBenchmark();
            this.fetchFirmProspects();
            this.fetchFirmProspectsLocationsData();
        },
        fetchYearFirms: function () {
            const year = this.dataYear;
            axios.get(`${this.ecosystemPrefix}/benchmarking/firms?benchmark[year]=${year}`)
            .then((response) => {
                let sortedFirms = response.data.sort((a, b) => {
                    let nameA = a.name.toLowerCase();
                    let nameB = b.name.toLowerCase();

                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0; // names are equal
                });

                this.allLawFirms = [...sortedFirms]
            })
        },
        toggleSaveModal: function () {
            this.showConfirm = !this.showConfirm;
        },
        toggleInfoModal: function () {
            this.showInfo = !this.showInfo;
        },
        saveBenchmark: function () {
            if (this.benchmarkName.length > 0) {
                axios.post(`${this.ecosystemPrefix}/save_benchmark`, {
                    saved_benchmark: {
                        name: this.benchmarkName,
                        benchmark_firm: this.benchmarkFirm,
                        comparison_firms: this.comparisonFirms,
                        year: this.dataYear
                    }
                }).then((response) => {
                    this.savedBenchmarks = response.data;
                    this.benchmarkName = "";
                    this.showConfirm = !this.showConfirm;
                    this.showSaveSuccess = true;
                    setTimeout(()=>{
                        this.showSaveSuccess = false;
                    }, 5000)
                })
            }
        },
        clearSelections: function () {
            this.benchmarkFirm = null;
            this.comparisonFirms = [];
            this.selectedSavedBenchmark = null;
            this.benchmarkFirmData = null;
            this.comparsionFirmData = null;
            this.allFirmData = null;
            this.benchmarkFirmProspects = null;
            this.comparisonFirmProspects = null;
            this.dataLocation = '';
            this.selectedPracticeArea = null;
            this.currentTab = 'overview';
        },
        print: function () {
            paperize([`${this.ecosystemPrefix}/benchmarking/print-styles`]);
        },
        estimateIndicator:  function (value) {
                if (typeof(value) === "string") {
                    let foundFirm = this.chartData.find(obj => obj.name === value);
                    return foundFirm.estimated_parts || foundFirm.estimated_attys ?
                        `${value} *`
                        :
                        `${value}`
                } else {
                   return `${value}`
                }
        },
        showTab: function(tab) {
            this.currentTab = tab;
        }
    },
    mounted: function () {
        this.fetchYearFirms();
        axios.get(`${this.ecosystemPrefix}/saved_benchmarks`)
            .then((response) => {
                this.savedBenchmarks = response.data.benchmarks;
                // this.showTeaser = response.data.teaser;
            })
        axios.get(`${this.ecosystemPrefix}/benchmarking/check-access`)
            .then((response) => {
                this.showTeaser = !response.data.access;
            })
        const subEl = document.querySelector('#pulse-subscriber[data-subscriber]');
        console.log(subEl)
        this.isSubscriber = !!(subEl && subEl.dataset.subscriber === 'true');
    },
    watch: {
        dataYear(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.fetchYearFirms();
            }
        }
    },
    computed: {
        ecosystemPrefix() {
            return (document.getElementById('ecosystem')?.value === 'true') ? '/legal-news' : '';
        },
        comparsionFirmsConcat() {
            let comparsionFirmNames = this.comparisonFirms.map((firm) => firm.name)
            return comparsionFirmNames.join(", ")
        },
        saveButtonDisabled() {
            return !(this.benchmarkFirm && (this.comparisonFirms.length > 0))
        },
        clearButtonDisabled() {
            return !(this.benchmarkFirm || (this.comparisonFirms.length > 0))
        },
        benchmarkFirmSanitized() {
            if (this.benchmarkFirmData) {
                return {
                    grossRevenue: this.benchmarkFirmData['grossrev_globe'] ? `${this.benchmarkFirmData['grossrev_globe']}` : null,
                    revenueChange: this.benchmarkFirmData.rev_change_perc ? `${this.benchmarkFirmData.rev_change_perc}%` : null,
                    netIncome: this.benchmarkFirmData.netinc_globe ? `${this.benchmarkFirmData.netinc_globe}` : null,
                    profitMargin: this.benchmarkFirmData.gross_profit_margin ? `${String(this.benchmarkFirmData.gross_profit_margin)}%` : null,
                    numberOfUSLawyers: this.benchmarkFirmData.total_ushc ? `${this.benchmarkFirmData.total_ushc}` : null,
                    numberOfUSPartners: this.benchmarkFirmData.part_ushc ? `${this.benchmarkFirmData.part_ushc}` : null,
                    percentFemale: this.benchmarkFirmData.fem_atty_pct ? `${this.benchmarkFirmData.fem_atty_pct}%` : null,
                    percentMinority: this.benchmarkFirmData.div_atty_pct ? `${this.benchmarkFirmData.div_atty_pct}%` : null,
                    percentLGBTQ: this.benchmarkFirmData.lgbtq_atty_pct ? `${this.benchmarkFirmData.lgbtq_atty_pct}%` : null,
                    percentProBono: this.benchmarkFirmData.partic_pct ? `${this.benchmarkFirmData.partic_pct}%` : null,
                    estimatedPartners: this.benchmarkFirmData.estimated_parts ? `${this.benchmarkFirmData.estimated_parts}%` : null,
                    estimatedAttys: this.benchmarkFirmData.estimated_attys ? `${this.benchmarkFirmData.estimated_attys}%` : null,
                    revenuePerLawyer: this.benchmarkFirmData.rpl ? `${this.benchmarkFirmData.rpl}` : null,
                    costPerLawyer: this.benchmarkFirmData.cpl ? `${this.benchmarkFirmData.cpl}` : null,
                    profitPerEquityPartner: this.benchmarkFirmData.ppep ? `${this.benchmarkFirmData.ppep}` : null,
                    leverage: this.benchmarkFirmData.lev ? `${this.benchmarkFirmData.lev}` : null,
                    equityPartnerPercentage: this.benchmarkFirmData.eq_part_perc ? `${this.benchmarkFirmData.eq_part_perc}%` : null,
                    totalAttorneys: this.benchmarkFirmData.total_attorneys ? `${this.benchmarkFirmData.total_attorneys}` : null,
                    totalUSAttorneysFirm: this.benchmarkFirmData.total_us_attorneys_firm ? `${this.benchmarkFirmData.total_us_attorneys_firm}` : null,
                    totalUSPartnersFirm: this.benchmarkFirmData.total_us_partners_firm ? `${this.benchmarkFirmData.total_us_partners_firm}` : null,
                    totalNonUSAttorneysFirm: this.benchmarkFirmData.total_non_us_attorneys_firm ? `${this.benchmarkFirmData.total_non_us_attorneys_firm}` : null,
                }
            }
        },
        comparisonFirmsSanitized() {
            if (this.comparsionFirmData) {
                return {
                    grossRevenueMean: this.comparsionFirmData['grossrev_globe_mean'] && this.comparsionFirmData['grossrev_globe_mean'] !== "n/a" ? `${this.comparsionFirmData['grossrev_globe_mean']}` : null,
                    grossRevenueMedian: this.comparsionFirmData['grossrev_globe_median'] && this.comparsionFirmData['grossrev_globe_median'] !== "n/a" ? `${this.comparsionFirmData['grossrev_globe_median']}` : null,
                    grossRevenueTrend: this.comparsionFirmData['grossrev_globe_mean_comp'] && this.comparsionFirmData['grossrev_globe_mean_comp'] !== "n/a" ? `${this.comparsionFirmData['grossrev_globe_mean_comp']}` : null,
                    grossRevenueVar: this.comparsionFirmData['grossrev_globe_mean_var'] && this.comparsionFirmData['grossrev_globe_mean_var'] !== "n/a" ? `${this.comparsionFirmData['grossrev_globe_mean_var']}` : null,
                    revenueChangeMean: this.comparsionFirmData.rev_change_perc_mean && this.comparsionFirmData.rev_change_perc_mean !== "n/a" ? `${this.comparsionFirmData.rev_change_perc_mean}%` : null,
                    revenueChangeMedian: this.comparsionFirmData.rev_change_perc_median && this.comparsionFirmData.rev_change_perc_median !== "n/a" ? `${this.comparsionFirmData.rev_change_perc_median}%` : null,
                    revenueChangeTrend: this.comparsionFirmData.rev_change_perc_mean_comp && this.comparsionFirmData.rev_change_perc_mean_comp !== "n/a" ? `${this.comparsionFirmData.rev_change_perc_mean_comp}` : null,
                    revenueChangeVar: this.comparsionFirmData.rev_change_perc_mean_var && this.comparsionFirmData.rev_change_perc_mean_var !== "n/a" ? `${this.comparsionFirmData.rev_change_perc_mean_var}%` : null,
                    netIncomeMean: this.comparsionFirmData.netinc_globe_mean && this.comparsionFirmData.netinc_globe_mean !== "n/a" ? `${this.comparsionFirmData.netinc_globe_mean}` : null,
                    netIncomeMedian: this.comparsionFirmData.netinc_globe_median && this.comparsionFirmData.netinc_globe_median !== "n/a" ? `${this.comparsionFirmData.netinc_globe_median}` : null,
                    netIncomeTrend: this.comparsionFirmData.netinc_globe_mean_comp && this.comparsionFirmData.netinc_globe_mean_comp !== "n/a" ? `${this.comparsionFirmData.netinc_globe_mean_comp}` : null,
                    netIncomeVar: this.comparsionFirmData.netinc_globe_mean_var && this.comparsionFirmData.netinc_globe_mean_var !== "n/a" ? `${this.comparsionFirmData.netinc_globe_mean_var}` : null,
                    profitMarginMean: this.comparsionFirmData.gross_profit_margin_mean && this.comparsionFirmData.gross_profit_margin_mean !== "n/a" ? `${String(this.comparsionFirmData.gross_profit_margin_mean)}%` : null,
                    profitMarginMedian: this.comparsionFirmData.gross_profit_margin_mean && this.comparsionFirmData.gross_profit_margin_median !== "n/a" ? `${String(this.comparsionFirmData.gross_profit_margin_median)}%` : null,
                    profitMarginTrend: this.comparsionFirmData.gross_profit_margin_mean && this.comparsionFirmData.gross_profit_margin_mean_comp !== "n/a" ? `${this.comparsionFirmData.gross_profit_margin_mean_comp}` : null,
                    profitMarginVar: this.comparsionFirmData.gross_profit_margin_mean && this.comparsionFirmData.gross_profit_margin_mean_var !== "n/a" ? `${String(this.comparsionFirmData.gross_profit_margin_mean_var)}%` : null,
                    numberOfUSLawyersMean: this.comparsionFirmData.total_ushc_mean && this.comparsionFirmData.total_ushc_mean !== "n/a" ? `${this.comparsionFirmData.total_ushc_mean}` : null,
                    numberOfUSLawyersMedian: this.comparsionFirmData.total_ushc_median && this.comparsionFirmData.total_ushc_median !== "n/a" ? `${this.comparsionFirmData.total_ushc_median}` : null,
                    numberOfUSLawyersTrend: this.comparsionFirmData.total_ushc_mean_comp && this.comparsionFirmData.total_ushc_mean_comp !== "n/a" ? `${this.comparsionFirmData.total_ushc_mean_comp}` : null,
                    numberOfUSLawyersVar: this.comparsionFirmData.total_ushc_mean_var && this.comparsionFirmData.total_ushc_mean_var !== "n/a" ? `${this.comparsionFirmData.total_ushc_mean_var}` : null,
                    numberOfUSPartnersMean: this.comparsionFirmData.part_ushc_mean && this.comparsionFirmData.part_ushc_mean !== "n/a" ? `${this.comparsionFirmData.part_ushc_mean}` : null,
                    numberOfUSPartnersMedian: this.comparsionFirmData.part_ushc_median && this.comparsionFirmData.part_ushc_median !== "n/a" ? `${this.comparsionFirmData.part_ushc_median}` : null,
                    numberOfUSPartnersTrend: this.comparsionFirmData.part_ushc_mean_comp && this.comparsionFirmData.part_ushc_mean_comp !== "n/a" ? `${this.comparsionFirmData.part_ushc_mean_comp}` : null,
                    numberOfUSPartnersVar: this.comparsionFirmData.part_ushc_mean_var && this.comparsionFirmData.part_ushc_mean_var !== "n/a" ? `${this.comparsionFirmData.part_ushc_mean_var}` : null,
                    percentFemaleMean: this.comparsionFirmData.fem_atty_pct_mean && this.comparsionFirmData.fem_atty_pct_mean !== "n/a" ? `${this.comparsionFirmData.fem_atty_pct_mean}%` : null,
                    percentFemaleMedian: this.comparsionFirmData.fem_atty_pct_median && this.comparsionFirmData.fem_atty_pct_median !== "n/a" ? `${this.comparsionFirmData.fem_atty_pct_median}%` : null,
                    percentFemaleTrend: this.comparsionFirmData.fem_atty_pct_mean_comp && this.comparsionFirmData.fem_atty_pct_mean_comp !== "n/a" ? `${this.comparsionFirmData.fem_atty_pct_mean_comp}` : null,
                    percentFemaleVar: this.comparsionFirmData.fem_atty_pct_mean_var && this.comparsionFirmData.fem_atty_pct_mean_var !== "n/a" ? `${this.comparsionFirmData.fem_atty_pct_mean_var}%` : null,
                    percentMinorityMean: this.comparsionFirmData.div_atty_pct_mean && this.comparsionFirmData.div_atty_pct_mean !== "n/a" ? `${this.comparsionFirmData.div_atty_pct_mean}%` : null,
                    percentMinorityMedian: this.comparsionFirmData.div_atty_pct_median && this.comparsionFirmData.div_atty_pct_median !== "n/a" ? `${this.comparsionFirmData.div_atty_pct_median}%` : null,
                    percentMinorityTrend: this.comparsionFirmData.div_atty_pct_mean_comp && this.comparsionFirmData.div_atty_pct_mean_comp !== "n/a" ? `${this.comparsionFirmData.div_atty_pct_mean_comp}` : null,
                    percentMinorityVar: this.comparsionFirmData.div_atty_pct_mean_var && this.comparsionFirmData.div_atty_pct_mean_var !== "n/a" ? `${this.comparsionFirmData.div_atty_pct_mean_var}%` : null,
                    percentLGBTQMean: this.comparsionFirmData.lgbtq_atty_pct_mean && this.comparsionFirmData.lgbtq_atty_pct_mean !== "n/a" ? `${this.comparsionFirmData.lgbtq_atty_pct_mean}%` : null,
                    percentLGBTQMedian: this.comparsionFirmData.lgbtq_atty_pct_median && this.comparsionFirmData.lgbtq_atty_pct_median !== "n/a" ? `${this.comparsionFirmData.lgbtq_atty_pct_median}%` : null,
                    percentLGBTQTrend: this.comparsionFirmData.lgbtq_atty_pct_mean_comp && this.comparsionFirmData.lgbtq_atty_pct_mean_comp !== "n/a" ? `${this.comparsionFirmData.lgbtq_atty_pct_mean_comp}` : null,
                    percentLGBTQVar: this.comparsionFirmData.lgbtq_atty_pct_mean_var && this.comparsionFirmData.lgbtq_atty_pct_mean_var !== "n/a" ? `${this.comparsionFirmData.lgbtq_atty_pct_mean_var}%` : null,
                    percentProBonoMean: this.comparsionFirmData.partic_pct_mean && this.comparsionFirmData.partic_pct_mean !== "n/a" ? `${this.comparsionFirmData.partic_pct_mean}%` : null,
                    percentProBonoMedian: this.comparsionFirmData.partic_pct_median && this.comparsionFirmData.partic_pct_median !== "n/a" ? `${this.comparsionFirmData.partic_pct_median}%` : null,
                    percentProBonoTrend: this.comparsionFirmData.partic_pct_mean_comp && this.comparsionFirmData.partic_pct_mean_comp !== "n/a" ? `${this.comparsionFirmData.partic_pct_mean_comp}` : null,
                    percentProBonoVar: this.comparsionFirmData.partic_pct_mean_var && this.comparsionFirmData.partic_pct_mean_var !== "n/a" ? `${this.comparsionFirmData.partic_pct_mean_var}%` : null,
                    // Revenue per Lawyer
                    revenuePerLawyerMean: this.comparsionFirmData.rpl_mean && this.comparsionFirmData.rpl_mean !== "n/a" ? `${this.comparsionFirmData.rpl_mean}` : null,
                    revenuePerLawyerMedian: this.comparsionFirmData.rpl_median && this.comparsionFirmData.rpl_median !== "n/a" ? `${this.comparsionFirmData.rpl_median}` : null,
                    revenuePerLawyerTrend: this.comparsionFirmData.rpl_mean_comp && this.comparsionFirmData.rpl_mean_comp !== "n/a" ? `${this.comparsionFirmData.rpl_mean_comp}` : null,
                    revenuePerLawyerVar: this.comparsionFirmData.rpl_mean_var && this.comparsionFirmData.rpl_mean_var !== "n/a" ? `${this.comparsionFirmData.rpl_mean_var}` : null,
                    // Cost per Lawyer
                    costPerLawyerMean: this.comparsionFirmData.cpl_mean && this.comparsionFirmData.cpl_mean !== "n/a" ? `${this.comparsionFirmData.cpl_mean}` : null,
                    costPerLawyerMedian: this.comparsionFirmData.cpl_median && this.comparsionFirmData.cpl_median !== "n/a" ? `${this.comparsionFirmData.cpl_median}` : null,
                    costPerLawyerTrend: this.comparsionFirmData.cpl_mean_comp && this.comparsionFirmData.cpl_mean_comp !== "n/a" ? `${this.comparsionFirmData.cpl_mean_comp}` : null,
                    costPerLawyerVar: this.comparsionFirmData.cpl_mean_var && this.comparsionFirmData.cpl_mean_var !== "n/a" ? `${this.comparsionFirmData.cpl_mean_var}` : null,
                    // Profit per Equity Partner
                    profitPerEquityPartnerMean: this.comparsionFirmData.ppep_mean && this.comparsionFirmData.ppep_mean !== "n/a" ? `${this.comparsionFirmData.ppep_mean}` : null,
                    profitPerEquityPartnerMedian: this.comparsionFirmData.ppep_median && this.comparsionFirmData.ppep_median !== "n/a" ? `${this.comparsionFirmData.ppep_median}` : null,
                    profitPerEquityPartnerTrend: this.comparsionFirmData.ppep_mean_comp && this.comparsionFirmData.ppep_mean_comp !== "n/a" ? `${this.comparsionFirmData.ppep_mean_comp}` : null,
                    profitPerEquityPartnerVar: this.comparsionFirmData.ppep_mean_var && this.comparsionFirmData.ppep_mean_var !== "n/a" ? `${this.comparsionFirmData.ppep_mean_var}` : null,
                    // Leverage
                    leverageMean: this.comparsionFirmData.lev_mean && this.comparsionFirmData.lev_mean !== "n/a" ? `${this.comparsionFirmData.lev_mean}` : null,
                    leverageMedian: this.comparsionFirmData.lev_median && this.comparsionFirmData.lev_median !== "n/a" ? `${this.comparsionFirmData.lev_median}` : null,
                    leverageTrend: this.comparsionFirmData.lev_mean_comp && this.comparsionFirmData.lev_mean_comp !== "n/a" ? `${this.comparsionFirmData.lev_mean_comp}` : null,
                    leverageVar: this.comparsionFirmData.lev_mean_var && this.comparsionFirmData.lev_mean_var !== "n/a" ? `${this.comparsionFirmData.lev_mean_var}` : null,
                    // Percentage of Partnership that is Equity Partners
                    equityPartnerPercentageMean: this.comparsionFirmData.eq_part_perc_mean && this.comparsionFirmData.eq_part_perc_mean !== "n/a" ? `${this.comparsionFirmData.eq_part_perc_mean}%` : null,
                    equityPartnerPercentageMedian: this.comparsionFirmData.eq_part_perc_median && this.comparsionFirmData.eq_part_perc_median !== "n/a" ? `${this.comparsionFirmData.eq_part_perc_median}%` : null,
                    equityPartnerPercentageTrend: this.comparsionFirmData.eq_part_perc_mean_comp && this.comparsionFirmData.eq_part_perc_mean_comp !== "n/a" ? `${this.comparsionFirmData.eq_part_perc_mean_comp}` : null,
                    equityPartnerPercentageVar: this.comparsionFirmData.eq_part_perc_mean_var && this.comparsionFirmData.eq_part_perc_mean_var !== "n/a" ? `${this.comparsionFirmData.eq_part_perc_mean_var}%` : null,
                    totalAttorneysMean: this.comparsionFirmData.total_attorneys_mean && this.comparsionFirmData.total_attorneys_mean !== "n/a" ? `${this.comparsionFirmData.total_attorneys_mean}` : null,
                    totalAttorneysMedian: this.comparsionFirmData.total_attorneys_median && this.comparsionFirmData.total_attorneys_median !== "n/a" ? `${this.comparsionFirmData.total_attorneys_median}` : null,
                    totalAttorneysTrend: this.comparsionFirmData.total_attorneys_mean_comp && this.comparsionFirmData.total_attorneys_mean_comp !== "n/a" ? `${this.comparsionFirmData.total_attorneys_mean_comp}` : null,
                    totalAttorneysVar: this.comparsionFirmData.total_attorneys_mean_var && this.comparsionFirmData.total_attorneys_mean_var !== "n/a" ? `${this.comparsionFirmData.total_attorneys_mean_var}` : null,
                    totalUSAttorneysFirmMean: this.comparsionFirmData.total_us_attorneys_firm_mean && this.comparsionFirmData.total_us_attorneys_firm_mean !== "n/a" ? `${this.comparsionFirmData.total_us_attorneys_firm_mean}` : null,
                    totalUSAttorneysFirmMedian: this.comparsionFirmData.total_us_attorneys_firm_median && this.comparsionFirmData.total_us_attorneys_firm_median !== "n/a" ? `${this.comparsionFirmData.total_us_attorneys_firm_median}` : null,
                    totalUSAttorneysFirmTrend: this.comparsionFirmData.total_us_attorneys_firm_mean_comp && this.comparsionFirmData.total_us_attorneys_firm_mean_comp !== "n/a" ? `${this.comparsionFirmData.total_us_attorneys_firm_mean_comp}` : null,
                    totalUSAttorneysFirmVar: this.comparsionFirmData.total_us_attorneys_firm_mean_var && this.comparsionFirmData.total_us_attorneys_firm_mean_var !== "n/a" ? `${this.comparsionFirmData.total_us_attorneys_firm_mean_var}` : null,
                    totalUSPartnersFirmMean: this.comparsionFirmData.total_us_partners_firm_mean && this.comparsionFirmData.total_us_partners_firm_mean !== "n/a" ? `${this.comparsionFirmData.total_us_partners_firm_mean}` : null,
                    totalUSPartnersFirmMedian: this.comparsionFirmData.total_us_partners_firm_median && this.comparsionFirmData.total_us_partners_firm_median !== "n/a" ? `${this.comparsionFirmData.total_us_partners_firm_median}` : null,
                    totalUSPartnersFirmTrend: this.comparsionFirmData.total_us_partners_firm_mean_comp && this.comparsionFirmData.total_us_partners_firm_mean_comp !== "n/a" ? `${this.comparsionFirmData.total_us_partners_firm_mean_comp}` : null,
                    totalUSPartnersFirmVar: this.comparsionFirmData.total_us_partners_firm_mean_var && this.comparsionFirmData.total_us_partners_firm_mean_var !== "n/a" ? `${this.comparsionFirmData.total_us_partners_firm_mean_var}` : null,
                    totalNonUSAttorneysFirmMean: this.comparsionFirmData.total_non_us_attorneys_firm_mean && this.comparsionFirmData.total_non_us_attorneys_firm_mean !== "n/a" ? `${this.comparsionFirmData.total_non_us_attorneys_firm_mean}` : null,
                    totalNonUSAttorneysFirmMedian: this.comparsionFirmData.total_non_us_attorneys_firm_median && this.comparsionFirmData.total_non_us_attorneys_firm_median !== "n/a" ? `${this.comparsionFirmData.total_non_us_attorneys_firm_median}` : null,
                    totalNonUSAttorneysFirmTrend: this.comparsionFirmData.total_non_us_attorneys_firm_mean_comp && this.comparsionFirmData.total_non_us_attorneys_firm_mean_comp !== "n/a" ? `${this.comparsionFirmData.total_non_us_attorneys_firm_mean_comp}` : null,
                    totalNonUSAttorneysFirmVar: this.comparsionFirmData.total_non_us_attorneys_firm_mean_var && this.comparsionFirmData.total_non_us_attorneys_firm_mean_var !== "n/a" ? `${this.comparsionFirmData.total_non_us_attorneys_firm_mean_var}` : null,
                }
            }
        },
        chartNames() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return [this.benchmarkFirmData.name].concat(this.allFirmData.map(firm => firm.name)).sort();
            } else {
                return []
            }
        },
        chartData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return [this.benchmarkFirmData].concat(this.allFirmData);
            } else {
                return []
            }
        },
        grossRevenueChartData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm['grossrev_globe'] : null;
                });
            } else {
                return []
            }
        },
        revenueChangeChartData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.rev_change_perc : null;
                });
            } else {
                return []
            }
        },
        netIncomeChartData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.netinc_globe : null;
                });
            } else {
                return []
            }
        },
        profitMarginChartData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.gross_profit_margin : null;
                });
            } else {
                return []
            }
        },
        usLawyerVsPartData() {
            let data = {
                usLawyers: [],
                usPartners: []
            };
            if (this.benchmarkFirmData && this.allFirmData) {
                this.chartNames.forEach(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);
                    data.usLawyers.push(foundFirm.total_ushc);
                    data.usPartners.push(foundFirm.part_ushc);
                });
                return data;
            } else {
                return []
            }
        },
        portionFemaleData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.fem_atty_pct : null;
                });
            } else {
                return []
            }
        },
        portionMinortyData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.div_atty_pct : null;
                });
            } else {
                return []
            }
        },
        portionLGBTQData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.lgbtq_atty_pct : null;
                });
            } else {
                return []
            }
        },
        portionProBonoData() {
            if (this.benchmarkFirmData && this.allFirmData) {
                return this.chartNames.map(firm => {
                    let foundFirm = this.chartData.find(obj => obj.name === firm);

                    return foundFirm ? foundFirm.partic_pct : null;
                });
            } else {
                return []
            }
        },
    }
}
</script>
<style lang="scss">
#navigation_new #navbar-center,
.navbar-center {
    max-width: 100%;
    width: 97%;
}

.starter-bg {
    background-image: url("../images/generic-bg.jpg");
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
}

.page-title {
    margin-top: 70px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.practice-areas-wrapper {
    max-height:900px;
    overflow-y: auto;
}

.practice-areas-table-header {
    position: sticky;
    top: 0;
}

.locations-wrapper {
    max-height:900px;
    overflow-y: auto;
}

.locations-table-header {
    position: sticky;
    top: 0;
}

.is-disabled {
    pointer-events: none;
    opacity: 0.45;
    cursor: not-allowed;
}

.dark #benchmark .modal-card,
.dark #benchmark .modal-card-head,
.dark #benchmark .modal-card-body,
.dark #benchmark .modal-card-foot {
    background-color: #00172e !important;
}

.dark #benchmark strong {
    color: #646f8c !important;
}


</style>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>

